import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import apiService, { PrereqArea } from '../services/api';

// Normaliza un tipo de documento para hacer match contra el catálogo de áreas del
// backend (tipo_documento → área): el mismo texto puede venir con distinto casing,
// tildes o espacios sobrantes entre la tabla de prerrequisitos y los documentos reales.
export const normalizeTipoDoc = (s?: string | null): string =>
  (s || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '') // quitar tildes
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ');

// Catálogo tipo_documento → área (RRHH/Prevención/N/A), desde
// "Documentos para acreditación.xlsx" ya sembrado en el backend. Es prácticamente
// estático (solo cambia si alguien reasigna un área en TestResponsablesPage), así que
// se cachea con staleTime largo y se comparte entre todas las vistas que lo usan
// (Estado de Documentación, detalle de personal) en vez de refetchear por cada una.
export const useAreaCatalog = () => {
  const query = useQuery({
    queryKey: ['prerrequisitos', 'catalogo-areas'],
    queryFn: async () => {
      const resp = await apiService.getCatalogoAreasPrerrequisitos();
      const rows = Array.isArray(resp?.data) ? resp.data : [];
      const map = new Map<string, PrereqArea>();
      rows.forEach(r => { if (r.tipo_documento) map.set(normalizeTipoDoc(r.tipo_documento), r.area); });
      return map;
    },
    staleTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Estable entre renders (memoizada por `query.data`) para poder usarla como dependencia
  // de un useMemo sin recalcular en cada render (ej. enriquecer miles de documentos).
  const catalogData = query.data;
  const getArea = useCallback((tipoDocumento?: string | null): PrereqArea | null => {
    if (!tipoDocumento) return null;
    return catalogData?.get(normalizeTipoDoc(tipoDocumento)) ?? null;
  }, [catalogData]);

  return { areaCatalog: catalogData ?? new Map<string, PrereqArea>(), getArea, isLoading: query.isLoading };
};

// Estilos compartidos para el badge de área, usados en cualquier vista que liste
// documentos (Estado de Documentación, detalle de personal).
export const AREA_BADGE_CLASSES: Record<PrereqArea, string> = {
  'RRHH': 'bg-violet-100 text-violet-800',
  'Prevención': 'bg-orange-100 text-orange-800',
  'N/A': 'bg-gray-100 text-gray-600',
};
